import { useCallback, useEffect, useRef, useState } from "react";

import type { AppSchema } from "@shared/types";

import {
  applyPatchToSchema,
  diffSchema,
  tryMergeRemotePatch,
  type JsonPatchOperation,
} from "@/lib/builder/operation";
import { useBuilderStore } from "@/lib/builder/builder-store";
import { resolveWsBaseUrl } from "@/lib/ws-url";

export interface Collaborator {
  id: string;
  name: string;
  color: string;
  cursor?: {
    pageId?: string;
    x: number;
    y: number;
  };
}

interface CollaborationState {
  connected: boolean;
  users: Collaborator[];
  remoteSchema: AppSchema | null;
  remoteSchemaUser: string | null;
}

export function useCollaboration(appId: string | null) {
  const [state, setState] = useState<CollaborationState>({
    connected: false,
    users: [],
    remoteSchema: null,
    remoteSchemaUser: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const pendingSendRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const serverSchemaRef = useRef<AppSchema | null>(null);
  const serverVersionRef = useRef<number>(0);
  const isRemoteRef = useRef(false);

  const schema = useBuilderStore((state) => state.schema);

  useEffect(() => {
    if (!appId) {
      setState({ connected: false, users: [], remoteSchema: null, remoteSchemaUser: null });
      serverSchemaRef.current = null;
      serverVersionRef.current = 0;
      return;
    }

    const token = localStorage.getItem("access_token");
    const params = token ? `?token=${encodeURIComponent(token)}` : "";
    const wsUrl = `${resolveWsBaseUrl()}/api/v1/apps/${appId}/collaborate${params}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setState((prev) => ({ ...prev, connected: true }));
      const currentSchema = useBuilderStore.getState().schema;
      serverSchemaRef.current = currentSchema;
      serverVersionRef.current = 0;
      ws.send(JSON.stringify({ type: "sync", schema: currentSchema, version: 0 }));
    };

    ws.onclose = () => {
      setState((prev) => ({ ...prev, connected: false, users: [] }));
      wsRef.current = null;
      serverSchemaRef.current = null;
      serverVersionRef.current = 0;
    };

    ws.onerror = () => {
      setState((prev) => ({ ...prev, connected: false }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as {
          type: string;
          users?: Collaborator[];
          schema?: AppSchema;
          patch?: JsonPatchOperation[];
          version?: number;
          name?: string;
          user_id?: string;
          data?: Collaborator["cursor"];
        };

        switch (msg.type) {
          case "presence":
            setState((prev) => ({ ...prev, users: msg.users || [] }));
            break;

          case "room_state":
            if (msg.schema !== undefined) {
              serverSchemaRef.current = msg.schema;
              serverVersionRef.current = msg.version ?? 0;
            }
            break;

          case "synced":
            if (msg.version !== undefined) {
              serverVersionRef.current = msg.version;
            }
            break;

          case "operation": {
            if (!msg.patch) break;
            const patch = msg.patch;
            const serverSchema = serverSchemaRef.current;
            if (serverSchema) {
              const nextServerSchema = applyPatchToSchema(serverSchema, patch);
              if (nextServerSchema) {
                serverSchemaRef.current = nextServerSchema;
              }
            }
            if (msg.version !== undefined) {
              serverVersionRef.current = msg.version;
            }

            isRemoteRef.current = true;
            const localSchema = useBuilderStore.getState().schema;
            const merge = tryMergeRemotePatch(localSchema, patch);
            if (merge.conflict) {
              setState((prev) => ({
                ...prev,
                remoteSchema: serverSchemaRef.current,
                remoteSchemaUser: msg.name || msg.user_id || "其他协作者",
              }));
            } else {
              useBuilderStore.getState().applyRemotePatch(patch);
              setState((prev) => ({ ...prev, remoteSchema: null, remoteSchemaUser: null }));
            }
            window.setTimeout(() => {
              isRemoteRef.current = false;
            }, 0);
            break;
          }

          case "conflict": {
            if (!msg.schema) break;
            const serverSchema = serverSchemaRef.current;
            const conflictSchema = msg.schema;
            const remotePatch = serverSchema
              ? diffSchema(serverSchema, conflictSchema)
              : [];

            serverSchemaRef.current = conflictSchema;
            serverVersionRef.current = msg.version ?? 0;

            isRemoteRef.current = true;
            const localSchema = useBuilderStore.getState().schema;
            const merge = remotePatch.length
              ? tryMergeRemotePatch(localSchema, remotePatch)
              : { schema: conflictSchema, conflict: false };

            if (merge.conflict) {
              setState((prev) => ({
                ...prev,
                remoteSchema: conflictSchema,
                remoteSchemaUser: msg.name || msg.user_id || "其他协作者",
              }));
            } else {
              useBuilderStore.getState().applyRemotePatch(remotePatch);
              setState((prev) => ({ ...prev, remoteSchema: null, remoteSchemaUser: null }));
            }
            window.setTimeout(() => {
              isRemoteRef.current = false;
            }, 0);
            break;
          }

          case "schema_change":
            if (msg.schema) {
              setState((prev) => ({
                ...prev,
                remoteSchema: msg.schema || null,
                remoteSchemaUser: msg.name || msg.user_id || "其他用户",
              }));
            }
            break;

          case "cursor":
            if (!msg.data) break;
            setState((prev) => ({
              ...prev,
              users: prev.users.map((user) =>
                user.id === msg.user_id ? { ...user, cursor: msg.data } : user,
              ),
            }));
            break;

          case "ping":
            ws.send(JSON.stringify({ type: "pong" }));
            break;
        }
      } catch {
        // ignore malformed messages
      }
    };

    return () => {
      ws.close();
      wsRef.current = null;
      serverSchemaRef.current = null;
      serverVersionRef.current = 0;
    };
  }, [appId]);

  // Send incremental JSON Patch operations when the local schema changes.
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (isRemoteRef.current) return;

    const serverSchema = serverSchemaRef.current;
    if (!serverSchema) return;
    if (serverSchema === schema) return;

    const patch = diffSchema(serverSchema, schema);
    if (patch.length === 0) return;

    if (pendingSendRef.current) {
      clearTimeout(pendingSendRef.current);
    }
    pendingSendRef.current = setTimeout(() => {
      const baseVersion = serverVersionRef.current;
      ws.send(JSON.stringify({ type: "operation", patch, baseVersion }));
      serverSchemaRef.current = schema;
      serverVersionRef.current = baseVersion + 1;
    }, 250);
  }, [schema]);

  const sendCursor = useCallback((data: NonNullable<Collaborator["cursor"]>) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "cursor", data }));
  }, []);

  const clearRemoteSchema = useCallback(() => {
    setState((prev) => ({ ...prev, remoteSchema: null, remoteSchemaUser: null }));
  }, []);

  return {
    ...state,
    sendCursor,
    clearRemoteSchema,
  };
}
